import React, { useState, useCallback, useRef } from 'react';
import { 
  AlertCircle, 
  CheckCircle, 
  Building2, 
  Settings,
  Droplet,
  Thermometer,
  Zap,
  Flame,
  Snowflake,
  Shield,
  MessageSquare,
  FileText,
  Loader2,
  Send,
  ChevronRight,
  ChevronLeft,
  Check,
  Upload,
  X,
  File,
  Truck,
  Image,
  Download,
  Calculator,
  Mail,
  Phone,
  Globe
} from 'lucide-react';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import SEO from '../components/SEO';
import { useAnalytics } from '../hooks/useAnalytics';

// Move component definitions outside to prevent re-creation on each render
const InputField = ({ label, field, type = "text", placeholder = "", required = false, className = "", formData, fieldErrors, handleInputChange }) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-medium text-gray-700">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <input
      type={type}
      value={formData[field]}
      onChange={(e) => handleInputChange(field, e.target.value)}
      placeholder={placeholder}
      className={`w-full px-4 py-3 rounded-lg border-2 transition-all duration-200 focus:outline-none focus:ring-0
        ${fieldErrors[field] 
          ? 'border-red-300 bg-red-50 text-red-900 placeholder-red-400' 
          : 'border-gray-200 bg-white hover:border-gray-300 focus:border-primary-500 focus:bg-primary-50'
        }`}
    />
    {fieldErrors[field] && (
      <div className="flex items-center gap-2 mt-1 text-red-600 text-sm">
        <AlertCircle className="w-4 h-4" />
        <span>{fieldErrors[field]}</span>
      </div>
    )}
  </div>
);

const SelectField = ({ label, field, options, required = false, className = "", formData, fieldErrors, handleInputChange }) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-medium text-gray-700">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <select
      value={formData[field]}
      onChange={(e) => handleInputChange(field, e.target.value)}
      className={`w-full px-4 py-3 rounded-lg border-2 transition-all duration-200 focus:outline-none focus:ring-0 bg-white
        ${fieldErrors[field] 
          ? 'border-red-300 bg-red-50 text-red-900' 
          : 'border-gray-200 hover:border-gray-300 focus:border-primary-500 focus:bg-primary-50'
        }`}
    >
      <option value="">Select {label.toLowerCase()}...</option>
      {options.map(option => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
    {fieldErrors[field] && (
      <div className="flex items-center gap-2 mt-1 text-red-600 text-sm">
        <AlertCircle className="w-4 h-4" />
        <span>{fieldErrors[field]}</span>
      </div>
    )}
  </div>
);

const TextAreaField = ({ label, field, rows = 3, required = false, className = "", formData, fieldErrors, handleInputChange }) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-medium text-gray-700">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <textarea
      value={formData[field]}
      onChange={(e) => handleInputChange(field, e.target.value)}
      rows={rows}
      className={`w-full px-4 py-3 rounded-lg border-2 transition-all duration-200 focus:outline-none focus:ring-0 resize-none
        ${fieldErrors[field] 
          ? 'border-red-300 bg-red-50 text-red-900 placeholder-red-400' 
          : 'border-gray-200 bg-white hover:border-gray-300 focus:border-primary-500 focus:bg-primary-50'
        }`}
    />
    {fieldErrors[field] && (
      <div className="flex items-center gap-2 mt-1 text-red-600 text-sm">
        <AlertCircle className="w-4 h-4" />
        <span>{fieldErrors[field]}</span>
      </div>
    )}
  </div>
);

const FileUploadField = ({ label, required = false, className = "", formData, fieldErrors, handleInputChange }) => {
  const [uploadState, setUploadState] = useState({ uploading: false, error: null });

  const handleFileUpload = async (file) => {
    if (!file) return;

    setUploadState({ uploading: true, error: null });

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('oilAnalysisPdf', file);

      const response = await axios.post('/api/upload/oil-analysis', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        handleInputChange('oilAnalysisPdf', response.data.filename);
        handleInputChange('oilAnalysisOriginalName', response.data.originalName);
        handleInputChange('oilAnalysisUploadedAt', response.data.uploadedAt);
        
        // Track successful file upload
        window.plausible && window.plausible('File Upload', {
          props: {
            type: 'oil_analysis_pdf',
            success: 'true',
            fileName: response.data.originalName
          }
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadState({ 
        uploading: false, 
        error: error.response?.data?.error || 'Upload failed' 
      });
      
      // Track failed file upload
      window.plausible && window.plausible('File Upload', {
        props: {
          type: 'oil_analysis_pdf',
          success: 'false',
          fileName: file.name || 'unknown'
        }
      });
      return;
    }

    setUploadState({ uploading: false, error: null });
  };

  const handleFileRemove = () => {
    handleInputChange('oilAnalysisPdf', '');
    handleInputChange('oilAnalysisOriginalName', '');
    handleInputChange('oilAnalysisUploadedAt', '');
  };

  const hasFile = formData.oilAnalysisPdf && formData.oilAnalysisOriginalName;

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      {!hasFile ? (
        <div className="relative">
          <input
            type="file"
            accept=".pdf"
            onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0])}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={uploadState.uploading}
          />
          <div className={`w-full px-4 py-6 rounded-lg border-2 border-dashed transition-all duration-200 flex flex-col items-center justify-center gap-3
            ${uploadState.uploading 
              ? 'border-blue-300 bg-blue-50' 
              : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
            }`}>
            {uploadState.uploading ? (
              <>
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                <p className="text-sm text-blue-600 font-medium">Uploading PDF...</p>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8 text-gray-400" />
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700">Upload Oil Analysis Report</p>
                  <p className="text-xs text-gray-500 mt-1">PDF files only, max 10MB</p>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-3">
            <File className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-sm font-medium text-green-800">{formData.oilAnalysisOriginalName}</p>
              <p className="text-xs text-green-600">Uploaded successfully</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleFileRemove}
            className="p-1 text-green-600 hover:text-green-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {uploadState.error && (
        <div className="flex items-center gap-2 mt-1 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{uploadState.error}</span>
        </div>
      )}
    </div>
  );
};

const ImageUploadField = ({ label, required = false, className = "", formData, fieldErrors, handleInputChange }) => {
  const [uploadState, setUploadState] = useState({ uploading: false, error: null, uploadProgress: {} });

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;

    const currentImages = formData.submissionImages || [];
    const totalImages = currentImages.length + files.length;
    
    if (totalImages > 10) {
      setUploadState(prev => ({ 
        ...prev, 
        error: `Cannot upload ${files.length} images. Maximum 10 images allowed (currently have ${currentImages.length})` 
      }));
      return;
    }

    setUploadState(prev => ({ ...prev, uploading: true, error: null }));
    const uploadProgress = {};

    try {
      const uploadPromises = Array.from(files).map(async (file, index) => {
        // Validate file size (20MB)
        if (file.size > 20 * 1024 * 1024) {
          throw new Error(`${file.name} is too large. Maximum file size is 20MB.`);
        }

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff'];
        if (!validTypes.includes(file.type)) {
          throw new Error(`${file.name} is not a valid image type. Supported formats: JPG, PNG, GIF, WebP, BMP, TIFF.`);
        }

        uploadProgress[index] = 0;
        setUploadState(prev => ({ ...prev, uploadProgress: { ...prev.uploadProgress, [index]: 0 } }));

        const formDataUpload = new FormData();
        formDataUpload.append('submissionImage', file);

        const response = await axios.post('/api/upload/submission-images', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            uploadProgress[index] = progress;
            setUploadState(prev => ({ ...prev, uploadProgress: { ...prev.uploadProgress, [index]: progress } }));
          }
        });

        if (response.data.success) {
          return {
            filename: response.data.filename,
            originalName: response.data.originalName,
            uploadedAt: response.data.uploadedAt,
            contentType: file.type,
            size: file.size
          };
        } else {
          throw new Error(`Failed to upload ${file.name}`);
        }
      });

      const uploadedImages = await Promise.all(uploadPromises);
      const updatedImages = [...currentImages, ...uploadedImages];
      handleInputChange('submissionImages', updatedImages);

    } catch (error) {
      console.error('Image upload error:', error);
      setUploadState(prev => ({ 
        ...prev, 
        uploading: false, 
        error: error.message || 'Failed to upload images' 
      }));
      return;
    }

    setUploadState({ uploading: false, error: null, uploadProgress: {} });
  };

  const handleImageRemove = (index) => {
    const currentImages = formData.submissionImages || [];
    const updatedImages = currentImages.filter((_, i) => i !== index);
    handleInputChange('submissionImages', updatedImages);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleImageUpload(files);
    }
  };

  const currentImages = formData.submissionImages || [];
  const canAddMore = currentImages.length < 10;

  return (
    <div className={`space-y-4 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* Upload Area */}
      {canAddMore && (
        <div className="relative">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => e.target.files && handleImageUpload(e.target.files)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={uploadState.uploading}
          />
          <div 
            className={`w-full px-4 py-6 rounded-lg border-2 border-dashed transition-all duration-200 flex flex-col items-center justify-center gap-3
              ${uploadState.uploading 
                ? 'border-purple-300 bg-purple-50' 
                : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
              }`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {uploadState.uploading ? (
              <>
                <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                <p className="text-sm text-purple-600 font-medium">Uploading images...</p>
                {Object.entries(uploadState.uploadProgress).map(([index, progress]) => (
                  <div key={index} className="w-full max-w-xs">
                    <div className="bg-purple-200 rounded-full h-2">
                      <div 
                        className="bg-purple-600 h-2 rounded-full transition-all duration-200"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <>
                <Image className="w-8 h-8 text-gray-400" />
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700">Upload Images</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Drag & drop or click to select • Max 10 images • 20MB each
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    JPG, PNG, GIF, WebP, BMP, TIFF supported
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Image Preview Grid */}
      {currentImages.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {currentImages.map((image, index) => (
            <div key={index} className="relative group">
              <div className="aspect-square bg-gray-100 rounded-lg border border-gray-200 overflow-hidden">
                <div className="w-full h-full flex items-center justify-center">
                  <Image className="w-8 h-8 text-gray-400" />
                </div>
                <div className="absolute inset-0 bg-green-500 bg-opacity-10 flex items-center justify-center">
                  <div className="text-center">
                    <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-xs text-green-800 font-medium">Uploaded</p>
                  </div>
                </div>
              </div>
              
              <button
                type="button"
                onClick={() => handleImageRemove(index)}
                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-sm opacity-0 group-hover:opacity-100"
              >
                <X className="w-3 h-3" />
              </button>
              
              <div className="mt-2">
                <p className="text-xs text-gray-600 truncate" title={image.originalName}>
                  {image.originalName}
                </p>
                <p className="text-xs text-gray-400">
                  {(image.size / 1024 / 1024).toFixed(1)}MB
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Status Messages */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500">
          {currentImages.length} of 10 images uploaded
        </span>
        {!canAddMore && (
          <span className="text-orange-600 font-medium">
            Maximum images reached
          </span>
        )}
      </div>

      {uploadState.error && (
        <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{uploadState.error}</span>
        </div>
      )}
    </div>
  );
};

const CCJensenForm = () => {
  const analytics = useAnalytics();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    company: '', address: '', phoneFax: '', contactPerson: '', email: '',
    machineType: '', machineName: '',
    oilType: '', oilName: '', viscosity: '', oilComments: '',
    systemVolume: '', tankVolume: '', tankComments: '',
    tempNormal: '', tempMax: '', tempMin: '',
    electricalVoltage: '', electricalHz: '', powerComments: '',
    preheatElectricalV: '', preheatElectricalHz: '', preheatComments: '',
    solidContaminants: '', solidContaminantsOther: '', solidIngressPerHour: '',
    waterContaminationPercent: '', waterContaminationPPM: '', waterIngressPerHour: '',
    protection: '', surfaceTreatment: '', approvals: '', contaminationLevel: '',
    generalComments: '',
    oilAnalysisPdf: '', oilAnalysisOriginalName: '', oilAnalysisUploadedAt: '',
    submissionImages: []
  });

  const [submitState, setSubmitState] = useState({
    loading: false,
    success: false,
    error: null
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [completedSteps, setCompletedSteps] = useState(new Set());

  // reCAPTCHA v2 state
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // ROI Modal state
  const [showROIModal, setShowROIModal] = useState(false);

  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    analytics.trackRecaptcha(true);
  };

  const onRecaptchaError = () => {
    setRecaptchaToken(null);
    analytics.trackRecaptcha(false);
  };

  const onRecaptchaExpired = () => {
    setRecaptchaToken(null);
    analytics.trackRecaptcha(false);
  };

  // ROI Modal handlers
  const handleROIModalOpen = () => {
    setShowROIModal(true);
    analytics.trackROIInterest('modal_open');
  };

  const handleROIModalClose = () => {
    setShowROIModal(false);
  };

  const steps = [
    {
      id: 0,
      title: 'Customer Info',
      description: 'Company details and contact information',
      icon: Building2,
      fields: ['company', 'address', 'phoneFax', 'contactPerson', 'email'],
      required: ['company', 'contactPerson', 'email']
    },
    {
      id: 1,
      title: 'Machine Details',
      description: 'Machine type and specifications',
      icon: Settings,
      fields: ['machineType', 'machineName'],
      required: []
    },
    {
      id: 2,
      title: 'Oil Information',
      description: 'Oil type, viscosity, and properties',
      icon: Droplet,
      fields: ['oilType', 'oilName', 'viscosity', 'oilComments'],
      required: []
    },
    {
      id: 3,
      title: 'System Specs',
      description: 'System volume and tank specifications',
      icon: Settings,
      fields: ['systemVolume', 'tankVolume', 'tankComments'],
      required: []
    },
    {
      id: 4,
      title: 'Temperature',
      description: 'Operating temperature ranges',
      icon: Thermometer,
      fields: ['tempNormal', 'tempMax', 'tempMin'],
      required: []
    },
    {
      id: 5,
      title: 'Power Supply',
      description: 'Electrical specifications',
      icon: Zap,
      fields: ['electricalVoltage', 'electricalHz', 'powerComments'],
      required: []
    },
    {
      id: 6,
      title: 'Heating',
      description: 'Preheat requirements',
      icon: Flame,
      fields: ['preheatElectricalV', 'preheatElectricalHz', 'preheatComments'],
      required: []
    },
    {
      id: 7,
      title: 'Current Condition',
      description: 'Present contamination levels',
      icon: AlertCircle,
      fields: ['solidContaminants', 'solidContaminantsOther', 'solidIngressPerHour', 'waterContaminationPercent', 'waterContaminationPPM', 'waterIngressPerHour'],
      required: []
    },
    {
      id: 8,
      title: 'Requirements',
      description: 'Customer demands and specifications',
      icon: Shield,
      fields: ['protection', 'surfaceTreatment', 'approvals', 'contaminationLevel'],
      required: []
    },
    {
      id: 9,
      title: 'Final Review',
      description: 'Comments and form submission',
      icon: MessageSquare,
      fields: ['generalComments'],
      required: []
    }
  ];

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  }, [fieldErrors]);

  const validateStep = (stepIndex) => {
    const step = steps[stepIndex];
    const errors = {};
    
    step.required.forEach(field => {
      if (!formData[field] || formData[field].trim() === '') {
        errors[field] = 'This field is required';
      }
    });

    if (step.required.includes('email') && formData.email) {
      if (!/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    return errors;
  };

  const handleNext = () => {
    const errors = validateStep(currentStep);
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    
    // Track form step completion
    analytics.trackFormStep(currentStep + 1, steps[currentStep].title);
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const previousStep = currentStep - 1;
      setCurrentStep(previousStep);
      analytics.trackFormStep(previousStep + 1, `${steps[previousStep].title} (revisited)`);
    }
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex <= currentStep || completedSteps.has(stepIndex)) {
      if (stepIndex !== currentStep) {
        analytics.trackFormStep(stepIndex + 1, `${steps[stepIndex].title} (jumped to)`);
      }
      setCurrentStep(stepIndex);
    }
  };

  const handleSubmit = async () => {
    let allErrors = {};
    steps.forEach(step => {
      const stepErrors = validateStep(step.id);
      allErrors = { ...allErrors, ...stepErrors };
    });

    if (Object.keys(allErrors).length > 0) {
      setFieldErrors(allErrors);
      const errorStep = steps.find(step => 
        step.required.some(field => allErrors[field])
      );
      if (errorStep) {
        setCurrentStep(errorStep.id);
      }
      return;
    }

    // Debug reCAPTCHA token before validation
    console.log('=== RECAPTCHA DEBUG START ===');
    console.log('reCAPTCHA token state:', recaptchaToken);
    console.log('reCAPTCHA token type:', typeof recaptchaToken);
    console.log('reCAPTCHA token length:', recaptchaToken ? recaptchaToken.length : 'N/A');
    console.log('reCAPTCHA ref current:', recaptchaRef.current);
    
    // Validate reCAPTCHA
    if (!recaptchaToken) {
      console.log('❌ reCAPTCHA validation failed - no token');
      setSubmitState({ 
        loading: false, 
        success: false, 
        error: 'Please complete the reCAPTCHA verification.' 
      });
      return;
    }

    console.log('✅ reCAPTCHA validation passed');
    console.log('=== RECAPTCHA DEBUG END ===');

    setSubmitState({ loading: true, success: false, error: null });
    
    // Debug: Log submission data
    console.log('=== FRONTEND SUBMISSION DEBUG ===');
    console.log('reCAPTCHA token being sent:', recaptchaToken ? `${recaptchaToken.substring(0, 20)}...` : 'NULL');
    console.log('PDF fields in formData before submission:');
    console.log('- oilAnalysisPdf:', formData.oilAnalysisPdf || 'NULL');
    console.log('- oilAnalysisOriginalName:', formData.oilAnalysisOriginalName || 'NULL');
    console.log('- oilAnalysisUploadedAt:', formData.oilAnalysisUploadedAt || 'NULL');
    console.log('Company:', formData.company);
    console.log('Contact:', formData.contactPerson);
    
    // Ensure reCAPTCHA token is included in submission
    const submissionData = {
      ...formData,
      recaptchaToken: recaptchaToken
    };
    
    console.log('Complete submission object keys:', Object.keys(submissionData));
    console.log('Submission object recaptchaToken:', submissionData.recaptchaToken ? `${submissionData.recaptchaToken.substring(0, 20)}...` : 'NULL');
    console.log('Explicit recaptchaToken check:', submissionData.hasOwnProperty('recaptchaToken'));
    console.log('=====================================');
    
    try {
      const response = await axios.post('/api/submit', {
        ...formData,
        recaptchaToken
      });
      
      if (response.data.success) {
        setSubmitState({ loading: false, success: true, error: null });
        setCompletedSteps(new Set([...Array(steps.length).keys()]));
        
        // Track successful form submission
        analytics.trackFormSubmission(true);
        
        setTimeout(() => {
          setFormData({
            company: '', address: '', phoneFax: '', contactPerson: '', email: '',
            machineType: '', machineName: '',
            oilType: '', oilName: '', viscosity: '', oilComments: '',
            systemVolume: '', tankVolume: '', tankComments: '',
            tempNormal: '', tempMax: '', tempMin: '',
            electricalVoltage: '', electricalHz: '', powerComments: '',
            preheatElectricalV: '', preheatElectricalHz: '', preheatComments: '',
            solidContaminants: '', solidContaminantsOther: '', solidIngressPerHour: '',
            waterContaminationPercent: '', waterContaminationPPM: '', waterIngressPerHour: '',
            protection: '', surfaceTreatment: '', approvals: '', contaminationLevel: '',
            generalComments: '',
            oilAnalysisPdf: '', oilAnalysisOriginalName: '', oilAnalysisUploadedAt: '',
            submissionImages: []
          });
          setCurrentStep(0);
          setCompletedSteps(new Set());
          setSubmitState({ loading: false, success: false, error: null });
          setRecaptchaToken(null);
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
        }, 5000);
      }
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to submit form. Please try again.';
      
      // Track failed form submission
      analytics.trackFormSubmission(false, errorMessage);
      
      if (error.response?.data?.error?.includes('reCAPTCHA')) {
        setSubmitState({ 
          loading: false, 
          success: false, 
          error: 'reCAPTCHA verification failed. Please try again.' 
        });
        // Reset reCAPTCHA
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setRecaptchaToken(null);
      } else {
        setSubmitState({ 
          loading: false, 
          success: false, 
          error: errorMessage
        });
      }
    }
  };


  const StepIndicator = () => (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Step {currentStep + 1} of {steps.length}</h2>
          <p className="text-gray-600 text-sm">{steps[currentStep].description}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-primary-600">{Math.round(((currentStep + 1) / steps.length) * 100)}%</div>
          <div className="text-xs text-gray-500">Complete</div>
        </div>
      </div>
      
      <div className="relative">
        <div className="overflow-hidden h-3 text-xs flex rounded-full bg-gray-200">
          <div 
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-500"
          />
        </div>
      </div>

      {/* Desktop: Grid layout, Mobile: Horizontal scroll */}
      <div className="mt-6">
        {/* Desktop Grid (md and up) */}
        <div className="hidden md:grid grid-cols-5 gap-3">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = completedSteps.has(index);
            const isAccessible = index <= currentStep || completedSteps.has(index);
            
            return (
              <button
                key={step.id}
                onClick={() => handleStepClick(index)}
                disabled={!isAccessible}
                className={`flex flex-col items-center space-y-2 p-3 rounded-lg transition-all duration-200
                  ${isActive 
                    ? 'bg-primary-50 text-primary-700 ring-2 ring-primary-200' 
                    : isCompleted 
                      ? 'bg-accent-50 text-accent-700 hover:bg-accent-100' 
                      : isAccessible
                        ? 'text-gray-600 hover:bg-gray-50'
                        : 'text-gray-400 cursor-not-allowed'
                  }`}
              >
                <div className={`p-2 rounded-lg ${
                  isActive 
                    ? 'bg-primary-100' 
                    : isCompleted 
                      ? 'bg-accent-100' 
                      : 'bg-gray-100'
                }`}>
                  {isCompleted && !isActive ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                </div>
                <span className="text-xs font-medium text-center">{step.title}</span>
              </button>
            );
          })}
        </div>

        {/* Mobile/Tablet: Horizontal scroll (md and below) */}
        <div className="flex md:hidden items-center justify-start overflow-x-auto gap-2 pb-2">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = completedSteps.has(index);
            const isAccessible = index <= currentStep || completedSteps.has(index);
            
            return (
              <button
                key={step.id}
                onClick={() => handleStepClick(index)}
                disabled={!isAccessible}
                className={`flex flex-col items-center space-y-2 p-3 rounded-lg transition-all duration-200 min-w-0 flex-shrink-0
                  ${isActive 
                    ? 'bg-primary-50 text-primary-700 ring-2 ring-primary-200' 
                    : isCompleted 
                      ? 'bg-accent-50 text-accent-700 hover:bg-accent-100' 
                      : isAccessible
                        ? 'text-gray-600 hover:bg-gray-50'
                        : 'text-gray-400 cursor-not-allowed'
                  }`}
              >
                <div className={`p-2 rounded-lg ${
                  isActive 
                    ? 'bg-primary-100' 
                    : isCompleted 
                      ? 'bg-accent-100' 
                      : 'bg-gray-100'
                }`}>
                  {isCompleted && !isActive ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                </div>
                <span className="text-xs font-medium text-center truncate w-16">{step.title}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <InputField
              label="Company Name"
              field="company"
              required
              placeholder="Enter your company name"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
            <InputField
              label="Business Address"
              field="address"
              placeholder="Enter your business address"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Phone / Fax Number"
                field="phoneFax"
                placeholder="e.g., +1 (555) 123-4567"
                formData={formData}
                fieldErrors={fieldErrors}
                handleInputChange={handleInputChange}
              />
              <InputField
                label="Contact Person"
                field="contactPerson"
                required
                placeholder="Enter contact person name"
                formData={formData}
                fieldErrors={fieldErrors}
                handleInputChange={handleInputChange}
              />
            </div>
            <InputField
              label="Email Address"
              field="email"
              type="email"
              required
              placeholder="Enter email address for confirmation"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
          </div>
        );

      case 1:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="Type of Machine"
              field="machineType"
              placeholder="e.g., Hydraulic press, CNC machine"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
            <InputField
              label="Machine Name/Model"
              field="machineName"
              placeholder="e.g., Model XYZ-1000"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Type of Oil"
                field="oilType"
                placeholder="e.g., Hydraulic oil, lubricating oil"
                formData={formData}
                fieldErrors={fieldErrors}
                handleInputChange={handleInputChange}
              />
              <InputField
                label="Oil Name/Brand"
                field="oilName"
                placeholder="e.g., Shell Tellus S2 M 46"
                formData={formData}
                fieldErrors={fieldErrors}
                handleInputChange={handleInputChange}
              />
            </div>
            <InputField
              label="Viscosity"
              field="viscosity"
              placeholder="e.g., ISO VG 46"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
            <TextAreaField
              label="Additional Oil Information"
              field="oilComments"
              placeholder="Any additional information about the oil..."
              rows={3}
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
          </div>
        );

      case 3:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="System Volume (Litres)"
              field="systemVolume"
              type="number"
              placeholder="e.g., 1000"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
            <InputField
              label="Tank Volume (Litres)"
              field="tankVolume"
              type="number"
              placeholder="e.g., 2000"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
            <div className="md:col-span-2">
              <TextAreaField
                label="Tank Information"
                field="tankComments"
                placeholder="Any additional tank specifications..."
                rows={3}
                formData={formData}
                fieldErrors={fieldErrors}
                handleInputChange={handleInputChange}
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField
              label="Operational Temperature (°C)"
              field="tempNormal"
              type="number"
              placeholder="e.g., 40"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
            <InputField
              label="Maximum Temperature (°C)"
              field="tempMax"
              type="number"
              placeholder="e.g., 80"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
            <InputField
              label="Start Up Temperature (°C)"
              field="tempMin"
              type="number"
              placeholder="e.g., 10"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Electrical Supply (V)"
                field="electricalVoltage"
                type="number"
                placeholder="e.g., 220"
                formData={formData}
                fieldErrors={fieldErrors}
                handleInputChange={handleInputChange}
              />
              <InputField
                label="Frequency (Hz)"
                field="electricalHz"
                type="number"
                placeholder="e.g., 50"
                formData={formData}
                fieldErrors={fieldErrors}
                handleInputChange={handleInputChange}
              />
            </div>
            <TextAreaField
              label="Power Supply Comments"
              field="powerComments"
              placeholder="Any additional power requirements or comments..."
              rows={3}
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
          </div>
        );

      case 6:
        return (
          <div className="space-y-8">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-4 flex items-center gap-3">
                <div className="p-2 bg-blue-200 rounded-lg">
                  <Zap className="w-5 h-5 text-blue-700" />
                </div>
                Electrical Preheat
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="Voltage (V)"
                  field="preheatElectricalV"
                  type="number"
                  placeholder="e.g., 240"
                  formData={formData}
                  fieldErrors={fieldErrors}
                  handleInputChange={handleInputChange}
                />
                <InputField
                  label="Frequency (Hz)"
                  field="preheatElectricalHz"
                  type="number"
                  placeholder="e.g., 50"
                  formData={formData}
                  fieldErrors={fieldErrors}
                  handleInputChange={handleInputChange}
                />
              </div>
            </div>



            <TextAreaField
              label="Preheat Comments"
              field="preheatComments"
              placeholder="Any additional preheat requirements..."
              rows={3}
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />

          </div>
        );

      case 7:
        return (
          <div className="space-y-8">
            <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl p-6 border border-yellow-200">
              <h4 className="font-semibold text-yellow-800 mb-4 flex items-center gap-3">
                <div className="p-2 bg-yellow-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-yellow-700" />
                </div>
                Solid Contaminants
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField
                  label="ISO/NAS Code"
                  field="solidContaminants"
                  placeholder="e.g., ISO 4406 20/18/15"
                  formData={formData}
                  fieldErrors={fieldErrors}
                  handleInputChange={handleInputChange}
                />
                <InputField
                  label="Other Classification"
                  field="solidContaminantsOther"
                  placeholder="Alternative classification system"
                  formData={formData}
                  fieldErrors={fieldErrors}
                  handleInputChange={handleInputChange}
                />
                <div className="md:col-span-2">
                  <InputField
                    label="Ingress Rate per Hour"
                    field="solidIngressPerHour"
                    placeholder="Rate of solid contamination ingress"
                    formData={formData}
                    fieldErrors={fieldErrors}
                    handleInputChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
              <h4 className="font-semibold text-purple-800 mb-4 flex items-center gap-3">
                <div className="p-2 bg-purple-200 rounded-lg">
                  <Droplet className="w-5 h-5 text-purple-700" />
                </div>
                Water Contamination
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InputField
                  label="Percentage (%)"
                  field="waterContaminationPercent"
                  type="number"
                  placeholder="e.g., 0.05"
                  formData={formData}
                  fieldErrors={fieldErrors}
                  handleInputChange={handleInputChange}
                />
                <InputField
                  label="PPM"
                  field="waterContaminationPPM"
                  type="number"
                  placeholder="e.g., 500"
                  formData={formData}
                  fieldErrors={fieldErrors}
                  handleInputChange={handleInputChange}
                />
                <InputField
                  label="Ingress per Hour"
                  field="waterIngressPerHour"
                  placeholder="Rate of water ingress"
                  formData={formData}
                  fieldErrors={fieldErrors}
                  handleInputChange={handleInputChange}
                />
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-4 flex items-center gap-3">
                <div className="p-2 bg-blue-200 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-700" />
                </div>
                Oil Analysis Report
              </h4>
              <FileUploadField
                label="Recent Oil Analysis Report (Optional)"
                formData={formData}
                fieldErrors={fieldErrors}
                handleInputChange={handleInputChange}
              />
              <p className="text-xs text-blue-600 mt-2">
                Upload your most recent oil analysis report to help us better understand your current system condition.
              </p>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="Protection Requirements"
              field="protection"
              placeholder="e.g., IP65 enclosure"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
            <InputField
              label="Surface Treatment"
              field="surfaceTreatment"
              placeholder="e.g., Powder coating, stainless steel"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
            <InputField
              label="Approvals / Classification"
              field="approvals"
              placeholder="e.g., ATEX, CE marking"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
            <InputField
              label="Contamination Level Target"
              field="contaminationLevel"
              placeholder="e.g., ISO 4406 16/14/11"
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
          </div>
        );

      case 9:
        return (
          <div className="space-y-6">
            <TextAreaField
              label="Additional Comments"
              field="generalComments"
              placeholder="Any additional requirements, specifications, or comments..."
              rows={5}
              formData={formData}
              fieldErrors={fieldErrors}
              handleInputChange={handleInputChange}
            />
            
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
              <h4 className="font-semibold text-purple-800 mb-4 flex items-center gap-3">
                <div className="p-2 bg-purple-200 rounded-lg">
                  <Image className="w-5 h-5 text-purple-700" />
                </div>
                Supporting Images
              </h4>
              <ImageUploadField
                label="Upload Images (Optional)"
                formData={formData}
                fieldErrors={fieldErrors}
                handleInputChange={handleInputChange}
              />
              <p className="text-xs text-purple-600 mt-2">
                Upload images of your current system, equipment, or any relevant documentation that might help us provide a more accurate quotation.
              </p>
            </div>
            
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-3">
                <div className="p-2 bg-gray-200 rounded-lg">
                  <FileText className="w-5 h-5 text-gray-700" />
                </div>
                Form Summary
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600">Company:</span>
                  <span className="ml-2 text-gray-800">{formData.company || 'Not specified'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Contact:</span>
                  <span className="ml-2 text-gray-800">{formData.contactPerson || 'Not specified'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Email:</span>
                  <span className="ml-2 text-gray-800">{formData.email || 'Not specified'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Machine Type:</span>
                  <span className="ml-2 text-gray-800">{formData.machineType || 'Not specified'}</span>
                </div>
              </div>
            </div>

            {/* reCAPTCHA */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-4 flex items-center gap-3">
                <div className="p-2 bg-blue-200 rounded-lg">
                  <Shield className="w-5 h-5 text-blue-700" />
                </div>
                Security Verification
              </h4>
              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6LdJ7corAAAAAHgZYGE2i8XOhgqsTDOyugNd63r9"
                  onChange={onRecaptchaChange}
                  onErrored={onRecaptchaError}
                  onExpired={onRecaptchaExpired}
                />
              </div>
              <p className="text-xs text-blue-600 mt-2 text-center">
                Please complete the verification to submit your inquiry
              </p>
            </div>

            {submitState.success && (
              <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-200 rounded-lg">
                    <CheckCircle className="text-green-700 w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-green-800 font-semibold text-lg">Form submitted successfully!</h3>
                    <p className="text-green-700 mt-1">
                      Thank you for your inquiry. We'll contact you within 2-3 business days.
                      A confirmation email has been sent to your email address.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {submitState.error && (
              <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-red-200 rounded-lg">
                    <AlertCircle className="text-red-700 w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-red-800 font-semibold text-lg">Submission failed</h3>
                    <p className="text-red-700 mt-1">{submitState.error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "CC Jensen System Inquiry",
    "description": "Custom oil and fuel filtration system quotation service for the Australian Mining industry",
    "provider": {
      "@type": "Organization",
      "name": "Mining and Hydraulics",
      "url": "https://yourwebsite.com.au/"
    },
    "serviceType": "Industrial Filtration Systems",
    "areaServed": "AU"
  };

  return (
    <>
      <SEO
        title="CC Jensen System Inquiry | Mining and Hydraulics"
        description="Get custom CC Jensen filtration system quotations for Australian mining operations. Submit technical specifications for hydraulic oil, gear oil, and fuel filtration solutions."
        keywords="CC Jensen inquiry, filtration system quotation, hydraulic oil filtration, mining filtration, gear oil filters, Australian mining, custom filtration solutions"
        url="https://yourwebsite.com.au/"
        type="website"
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-primary-50 to-accent-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-xl shadow-lg mb-8 p-8 relative overflow-hidden">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <img 
                src="/logo.png" 
                alt="CC Jensen industrial filtration systems logo - Mining and Hydraulics Australia" 
                className="w-20 h-20 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <FileText className="w-20 h-20 text-white hidden" />
              <div className="text-white text-center flex-1 mx-8">
                <h1 className="text-3xl font-bold">CC Jensen System Inquiry</h1>
                <p className="text-primary-100 mt-1">Infomation For Filter Quotation</p>
              </div>
              <img 
                src="/logo2.png" 
                alt="CC Jensen Logo 2" 
                className="w-20 h-20 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <FileText className="w-20 h-20 text-white hidden" />
            </div>
            <p className="text-primary-100 text-sm max-w-2xl leading-relaxed text-center mx-auto mb-4">
              Submit our comprehensive technical form to receive a custom-engineered filtration proposal. Our expert engineering team will evaluate your specifications and provide detailed system recommendations within 2-3 business days.
            </p>
            
            {/* Alternative navigation hint */}
            <div className="text-center space-y-3">
              <p className="text-primary-200 text-xs">
                Looking for available hire equipment? <Link to="/hire" className="text-white underline hover:text-primary-100">Browse our equipment selector</Link>
              </p>
              
              {/* ROI Business Case Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleROIModalOpen}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg border border-white/20 hover:border-white/40 transition-all duration-200 text-sm font-medium backdrop-blur-sm"
                >
                  <Calculator className="w-4 h-4" />
                  <span>Request a Business case / ROI</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <StepIndicator />

        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-8 py-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                {React.createElement(steps[currentStep].icon, { 
                  className: "w-6 h-6 text-primary-600" 
                })}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  {steps[currentStep].title}
                </h2>
                <p className="text-gray-600 text-sm">
                  {steps[currentStep].description}
                </p>
              </div>
            </div>
          </div>

          <div className="p-8">
            {renderStepContent()}
          </div>
        </div>

        <div className="flex items-center justify-between mt-8">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200
              ${currentStep === 0 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm hover:shadow-md'
              }`}
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Previous</span>
          </button>

          <div className="flex items-center gap-3">
            {currentStep < steps.length - 1 ? (
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 font-medium shadow-md hover:shadow-lg transition-all duration-200"
              >
                <span>Next Step</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitState.loading}
                className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-accent-600 to-accent-700 text-white rounded-xl hover:from-accent-700 hover:to-accent-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed font-medium shadow-md hover:shadow-lg transition-all duration-200"
              >
                {submitState.loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Submit Inquiry</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* ROI Modal */}
      {showROIModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-t-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold flex items-center gap-2">
                    <Calculator className="w-6 h-6" />
                    Request a Business Case / ROI
                  </h3>
                  <p className="text-green-100 mt-2">
                    Get professional ROI analysis and business case documentation for your filtration investment
                  </p>
                </div>
                <button
                  onClick={handleROIModalClose}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Service Description */}
              <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-800 mb-3">What We Provide</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li>• Comprehensive ROI calculations based on your specific operation</li>
                      <li>• Cost-benefit analysis including maintenance savings</li>
                      <li>• Equipment lifecycle and payback period projections</li>
                      <li>• Professional documentation for capital approval processes</li>
                      <li>• Comparison with alternative solutions</li>
                    </ul>
                  </div>
                  <div className="flex flex-col items-center">
                    <h5 className="font-medium text-green-800 mb-3 text-sm">Sample Report Preview</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-lg">
                      {/* Report Cover */}
                      <div className="flex flex-col items-center">
                        <div className="relative group cursor-pointer">
                          <img
                            src="/example-roi-report.png"
                            alt="ROI Report Cover - Professional business case documentation cover page"
                            className="w-full max-w-[120px] h-auto rounded-lg border border-green-300 shadow-md hover:shadow-lg transition-all duration-200 group-hover:scale-105"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="hidden w-full max-w-[120px] h-20 bg-green-100 border border-green-300 rounded-lg items-center justify-center">
                            <div className="text-center text-green-600">
                              <FileText className="w-5 h-5 mx-auto mb-1" />
                              <p className="text-xs">Report<br />Cover</p>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-green-600 mt-1 text-center">Report Cover</p>
                      </div>
                      
                      {/* Report Content */}
                      <div className="flex flex-col items-center">
                        <div className="relative group cursor-pointer">
                          <img
                            src="/example-roi-report2.png"
                            alt="ROI Report Data Page - Detailed ROI calculations and analysis"
                            className="w-full max-w-[120px] h-auto rounded-lg border border-green-300 shadow-md hover:shadow-lg transition-all duration-200 group-hover:scale-105"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="hidden w-full max-w-[120px] h-20 bg-green-100 border border-green-300 rounded-lg items-center justify-center">
                            <div className="text-center text-green-600">
                              <Calculator className="w-5 h-5 mx-auto mb-1" />
                              <p className="text-xs">Data &<br />Analysis</p>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-green-600 mt-1 text-center">Data & Analysis</p>
                      </div>
                      
                      {/* Case Studies */}
                      <div className="flex flex-col items-center sm:col-span-2 lg:col-span-1">
                        <div className="relative group cursor-pointer">
                          <img
                            src="/example-roi-report3.png"
                            alt="Case Studies - Real-world ROI examples and success stories"
                            className="w-full max-w-[120px] h-auto rounded-lg border border-green-300 shadow-md hover:shadow-lg transition-all duration-200 group-hover:scale-105"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="hidden w-full max-w-[120px] h-20 bg-green-100 border border-green-300 rounded-lg items-center justify-center">
                            <div className="text-center text-green-600">
                              <Building2 className="w-5 h-5 mx-auto mb-1" />
                              <p className="text-xs">Case<br />Studies</p>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-green-600 mt-1 text-center">Case Studies</p>
                      </div>
                    </div>
                    <p className="text-xs text-green-600 mt-3 text-center">Professional documentation & real-world examples</p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-6">
                <div className="text-center">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">Contact Our ROI Specialists</h4>
                  <p className="text-gray-600 mb-6">
                    Our experienced team will work with you to develop a compelling business case that demonstrates 
                    the value of CC Jensen filtration systems for your specific application.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Phone Contact */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Phone className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900">Phone</h5>
                      <p className="text-gray-700">(08) 9249 2511</p>
                      <p className="text-sm text-gray-500">Mon-Fri, 8:00 AM - 5:00 PM AWST</p>
                    </div>
                  </div>

                  {/* Email Contact */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Mail className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900">Email</h5>
                      <p className="text-gray-700">sales@mhs.com.au</p>
                      <p className="text-sm text-gray-500">Response within 24 hours</p>
                    </div>
                  </div>

                  {/* Website */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Globe className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900">Website</h5>
                      <p className="text-gray-700">miningandhydraulics.com.au</p>
                      <p className="text-sm text-gray-500">Online resources and case studies</p>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Building2 className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900">Location</h5>
                      <p className="text-gray-700">Perth, Western Australia</p>
                      <p className="text-sm text-gray-500">Serving all of Australia</p>
                    </div>
                  </div>
                </div>

                {/* Process Information */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-3">Our ROI Process</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-700">
                    <div className="text-center">
                      <div className="w-8 h-8 bg-blue-200 text-blue-800 rounded-full flex items-center justify-center mx-auto mb-2 font-semibold">1</div>
                      <p className="font-medium">Initial Consultation</p>
                      <p className="text-xs">Understand your operation & requirements</p>
                    </div>
                    <div className="text-center">
                      <div className="w-8 h-8 bg-blue-200 text-blue-800 rounded-full flex items-center justify-center mx-auto mb-2 font-semibold">2</div>
                      <p className="font-medium">Data Analysis</p>
                      <p className="text-xs">Calculate costs, savings & payback periods</p>
                    </div>
                    <div className="text-center">
                      <div className="w-8 h-8 bg-blue-200 text-blue-800 rounded-full flex items-center justify-center mx-auto mb-2 font-semibold">3</div>
                      <p className="font-medium">Business Case</p>
                      <p className="text-xs">Professional report for approval</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={handleROIModalClose}
                  className="px-6 py-2 bg-gray-300 text-gray-700 hover:bg-gray-400 focus:ring-gray-500 rounded-lg transition-colors"
                >
                  Close
                </button>
                <a
                  href="tel:+61892492511"
                  onClick={() => analytics.trackROIInterest('phone_click')}
                  className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg transition-all duration-200 font-medium"
                >
                  <Phone className="w-4 h-4" />
                  Call Now
                </a>
                <a
                  href="mailto:sales@mhs.com.au?subject=Business%20Case%2FROI%20Request&body=Hello%2C%0A%0AI%20would%20like%20to%20request%20a%20business%20case%20and%20ROI%20analysis%20for%20CC%20Jensen%20filtration%20systems.%0A%0APlease%20contact%20me%20to%20discuss%20my%20requirements.%0A%0AThank%20you."
                  onClick={() => analytics.trackROIInterest('email_click')}
                  className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg transition-all duration-200 font-medium"
                >
                  <Mail className="w-4 h-4" />
                  Send Email
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CCJensenForm;
